import { CircularProgress, Button as MUIButton } from '@mui/material';
import type { MouseEvent } from 'react';
import { forwardRef } from 'react';
import { Link as CustomLink } from '~/components/atoms/Link';
import type { ButtonProps } from './Button.types';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, loading, to, disabled, onClick, target, rel, renderAsLink, color, ...props }, ref) => {
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        event.preventDefault();
      } else if (onClick) {
        onClick(event);
      }
    };

    const customProps: ButtonProps = {
      ...props,
      color,
      sx: ({ palette }) => ({
        ...props.sx,
        gap: 2,
        ...(renderAsLink
          ? {
              fontWeight: 600,
              textDecoration: 'none',
              color: color ?? `${palette.text.primary} !important`,
              p: '0 !important',
              gap: loading ? 2 : 0,
              whiteSpace: 'nowrap',
              minWidth: !loading ? 'max-content' : undefined,
              ':hover': {
                p: 0,
                textDecoration: 'underline',
                cursor: 'pointer',
                color: `${palette.primary.main} !important`,
                background: 'transparent !important',
              },
            }
          : {}),
      }),
    };

    if (to) {
      return (
        <MUIButton
          component={CustomLink}
          ref={ref}
          to={to}
          disabled={disabled || loading}
          target={target}
          rel={rel}
          onClick={(event: MouseEvent<HTMLButtonElement>) => handleClick(event)}
          {...customProps}
        >
          {children}
        </MUIButton>
      );
    }

    return (
      <MUIButton
        ref={ref}
        disabled={disabled || loading}
        onClick={(event: MouseEvent<HTMLButtonElement>) => handleClick(event)}
        {...customProps}
      >
        {children}
        {loading && <CircularProgress data-testid="button-spinner" size={16} />}
      </MUIButton>
    );
  },
);

Button.displayName = 'Button';
